import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = ["title"];
import { computed, ref } from 'vue';
import moment from 'moment';
import { debounce } from 'lodash';
import alerts from "@astina/paloma-common-ui/src/services/Alerts";
import ArticleQuantityInput from "@astina/paloma-shop-common-ui/src/components/ArticleQuantityInput.vue";
import productUtils from "@astina/paloma-shop-common-ui/src/services/ProductUtils";
import DemandDayCutoff from "./DemandDayCutoff.vue";
import DemandDayDelivery from "./DemandDayDelivery.vue";
import trans from "@astina/paloma-common-ui/src/services/Translator";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DemandDayInput',
    props: {
        date: {},
        variant: {},
        now: {},
        customerDisabled: { type: Boolean }
    },
    emits: ['quantityChange'],
    setup(__props, { emit: __emit }) {
        const emit = __emit;
        const props = __props;
        /**
         * For performance reason, we only update 'now'
         * if it concerns the current day.
         */
        const now_initial = new Date(props.now.getTime());
        const now_ = computed(() => {
            if (!deliveryOption.value) {
                return now_initial;
            }
            if (moment(deliveryOption.value.latestOrderTime).isSame(moment(), 'day')) {
                return props.now;
            }
            return now_initial;
        });
        const deliveryOption = computed(() => {
            // there could be multiple options per date, but currently we only support one slot per date
            const opts = props.variant.shippingOptions.deliveryOptions
                .find(opt => opt.deliveryDate === props.date);
            if (!opts) {
                return null;
            }
            return opts;
        });
        // 'true' if this is the next available delivery option
        const nextDeliveryOption = computed(() => {
            if (!deliveryOption.value) {
                return false;
            }
            const futureShippingOptions = props.variant.shippingOptions.deliveryOptions.filter(opt => {
                const hasDeliveryItem = props.variant.deliveryItems.some(item => item.targetDate === opt.deliveryDate);
                if (hasDeliveryItem) {
                    return false;
                }
                return moment(opt.latestOrderTime).isAfter(now_.value);
            });
            if (futureShippingOptions.length === 0) {
                return false;
            }
            return deliveryOption.value.deliveryDate === futureShippingOptions[0].deliveryDate;
        });
        const demandItem = computed(() => {
            return props.variant.demandItems.find(item => item.targetDate === props.date);
        });
        const deliveryItems = computed(() => {
            return props.variant.deliveryItems.filter(item => item.targetDate === props.date);
        });
        const quantityConfig = computed(() => {
            if (!deliveryOption.value) {
                return null;
            }
            const variant = props.variant.variant;
            if (variant.orderingUnits.length === 0) {
                return null;
            }
            const variantDetails = createVariantDetails(variant);
            const initial = demandItem.value ?
                parseFloat(demandItem.value.quantity) : 0;
            const config = productUtils.createQuantityConfig(initial, variantDetails);
            // cannot set min => validated on quantity update
            config.min = 0;
            return config;
        });
        const inputInvalid = ref(false);
        const available = computed(() => {
            if (!quantityConfig.value) {
                return false;
            }
            if (!deliveryOption.value) {
                return false;
            }
            return moment(deliveryOption.value.latestOrderTime)
                .isAfter(moment(now_.value));
        });
        /**
         * Demand can only be entered if the delivery option is still open AND the
         * customer is allowed to order at all.
         */
        const inputEnabled = computed(() => {
            return available.value && !props.customerDisabled;
        });
        const cutoffToday = computed(() => {
            return !!(deliveryOption.value
                && moment(deliveryOption.value.latestOrderTime).isSame(moment(now_.value), 'day'));
        });
        const cutoffSoon = computed(() => {
            if (!deliveryOption.value || !cutoffToday.value) {
                return false;
            }
            const cutoff = moment(deliveryOption.value.latestOrderTime);
            return cutoff.isAfter(moment(now_.value))
                && cutoff.isSame(moment(now_.value), 'day')
                && moment(cutoff).isBefore(moment(now_.value).add(15, 'minute'));
        });
        function onQuantityChange(quantity) {
            const config = productUtils.createQuantityConfig(quantity.quantity, createVariantDetails(props.variant.variant));
            if (config.min && quantity.quantity > 0 && quantity.quantity < config.min) {
                inputInvalid.value = true;
                alerts.warning(trans('demand_item.min_quantity_undercut', { min: config.min }));
                return;
            }
            inputInvalid.value = false;
            // TODO unit?
            emit('quantityChange', quantity);
        }
        const onQuantityChangeDebounced = debounce(onQuantityChange, 500);
        function createVariantDetails(variant) {
            return {
                sku: variant.id,
                itemNumber: variant.itemNumber || variant.id,
                name: variant.title,
                price: variant.price,
                baseOrderingUnit: "", // TODO
                baseUnit: "", // TODO
                orderingUnits: variant.orderingUnits,
                units: [],
            };
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["demand-day-input", {
                        'demand-day-input--cutoff-soon': cutoffSoon.value && inputEnabled.value,
                        'demand-day-input--unavailable': !inputEnabled.value,
                        'demand-day-input--invalid': inputInvalid.value,
                        'demand-day-input--with-demand': (demandItem.value && parseInt(demandItem.value.quantity) > 0)
                    }])
            }, [
                (deliveryItems.value.length > 0)
                    ? (_openBlock(), _createBlock(DemandDayDelivery, {
                        key: 0,
                        "delivery-items": deliveryItems.value
                    }, null, 8, ["delivery-items"]))
                    : (deliveryOption.value)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 1,
                            class: "demand-day-input__form",
                            title: __props.customerDisabled
                                ? _ctx.$t('demand.customer_disabled')
                                : (available.value || !deliveryOption.value.latestOrderTime ? '' : _ctx.$dateTime(deliveryOption.value.latestOrderTime))
                        }, [
                            (quantityConfig.value && (available.value || cutoffToday.value))
                                ? (_openBlock(), _createBlock(ArticleQuantityInput, {
                                    key: 0,
                                    config: quantityConfig.value,
                                    available: inputEnabled.value,
                                    onChange: _unref(onQuantityChangeDebounced),
                                    "display-portions": "left",
                                    "reset-on-cart-item-added": false,
                                    "reset-portions-on-close": false
                                }, null, 8, ["config", "available", "onChange"]))
                                : _createCommentVNode("", true),
                            _cache[0] || (_cache[0] = _createTextVNode()),
                            (nextDeliveryOption.value && deliveryOption.value?.latestOrderTime && available.value)
                                ? (_openBlock(), _createBlock(DemandDayCutoff, {
                                    key: 1,
                                    date: deliveryOption.value.latestOrderTime,
                                    imminent: cutoffSoon.value,
                                    class: "demand-day-input__cutoff"
                                }, null, 8, ["date", "imminent"]))
                                : _createCommentVNode("", true)
                        ], 8, _hoisted_1))
                        : _createCommentVNode("", true)
            ], 2));
        };
    }
});
